"use client";

import { useSearchParams } from "next/navigation";
import { HeaderComponent } from "../../../../components/pages/robot/components/header/HeaderComponent";
import { IPageProps } from "@portal/types/robot/IPageProps";
import {
  StartPage,
  ExperiencePage,
  RelevencePage,
  RefreshPage,
  PlanningPage,
  RiskPage,
  BehaviourPage,
  SustainabilityPage,
  LiquidityPage,
  SavingsPage,
  SharesPage,
  RiskLevelPage,
  AgreementDetailsPage,
  SavingSuggestionPage,
  ExpectedValuePage,
  CompleteSavingsPage,
  FinalPage,
  DebtPage,
} from "../../../../components/pages/robot";
import { useAppDispatch, useAppSelector } from "@portal/redux/store";
import { FC, useEffect, useState } from "react";
import { IsBankNewNorwegian } from "@portal/helpers";
import { setLanguage } from "@portal/redux/reducers/languageReducer";
import { SendMessageForResize } from "@portal/components/pages/robot/components/helpers/SendMessageForResize";
import { ISavingsRobotPageClientComponentProps } from "./types";
import PageSpinner from "@portal/components/molecules/v2/pageSpinner";
import LayoutNotAuthorized from "@portal/components/organisms/layouts/Layout-NotAuthorized";
import useCenterIdOpenPages from "@portal/hooks/useCenterIdOpenPages";
import Layout from "@portal/components/organisms/layouts/Layout";
import PortalLayout from "@portal/components/organisms/layouts/portal";
import UseAddThemeToBodyElement from "@portal/hooks/useAddThemeToBodyElement";
import { SuitabilityReportPage } from "@portal/components/pages/robot/suitabilityReport";
import SWRConfigWrapper from "@portal/components/organisms/layouts/swrConfigWrapper";

/**
 * Component to distribute the pages in the savings robot.
 * @returns a page.
 */
const SavingsRobotPageClientComponent: FC<ISavingsRobotPageClientComponentProps> = ({ session, id }) => {
  const [isIFrame, setIsIFrame] = useState(false);
  const searchParams = useSearchParams();
  const dispatch = useAppDispatch();
  const isDefaultLanguage = useAppSelector(state => state.persisted.language.isDefaultLanguage);
  const centerId = useCenterIdOpenPages();

  // id for each page
  if (!IsValidId(id)) id = "0";

  useEffect(() => {
    setIsIFrame(inIframe());
  }, []);

  // Set default language
  useEffect(() => {
    if (centerId && IsBankNewNorwegian(centerId)) {
      dispatch(setLanguage(false));
    }
  }, [dispatch, centerId]);

  UseAddThemeToBodyElement({ centerId });

  useEffect(() => {
    SendMessageForResize();
  }, [id, isDefaultLanguage]);

  const status = searchParams?.get("status") as string;

  const renderRobot = () => (
    <div
      className="flex flex-col items-center m-auto mt-[1px] w-full min-w-64 max-w-3xl border border-grey border-t-0 rounded-xl bg-greyLight text-blueDark"
      id="sparerobot"
    >
      <HeaderComponent pageIndex={Number(id)} centerId={centerId} isDefaultLanguage={isDefaultLanguage} isIFrame={isIFrame} />
      {GetPage({ centerId, pageIndex: Number(id), isDefaultLanguage: isDefaultLanguage }, status)}
    </div>
  );

  if (status === "loading") {
    return <PageSpinner />;
  }

  if (isIFrame) {
    return <div className={`flex w-full bg-white min-h-[100vh]`}>{renderRobot()}</div>;
  }

  if (Number(id) > 11) return <PortalLayout session={session}>{renderRobot()}</PortalLayout>;

  return session?.status === "authenticated" ? (
    <SWRConfigWrapper>
      <Layout session={session}>{renderRobot()}</Layout>
    </SWRConfigWrapper>
  ) : (
    <LayoutNotAuthorized centerId={centerId}>{renderRobot()}</LayoutNotAuthorized>
  );
};

function GetPage({ centerId, pageIndex, isDefaultLanguage }: IPageProps, status?: string) {
  switch (pageIndex) {
    case 0:
      return <StartPage isDefaultLanguage={isDefaultLanguage} centerId={centerId} pageIndex={pageIndex} />;
    case 1:
      return <ExperiencePage isDefaultLanguage={isDefaultLanguage} centerId={centerId} pageIndex={pageIndex} />;
    case 2:
      return <RelevencePage isDefaultLanguage={isDefaultLanguage} centerId={centerId} pageIndex={pageIndex} />;
    case 3:
      return <RefreshPage isDefaultLanguage={isDefaultLanguage} centerId={centerId} pageIndex={pageIndex} />;
    case 4:
      return <PlanningPage isDefaultLanguage={isDefaultLanguage} centerId={centerId} pageIndex={pageIndex} />;
    case 5:
      return <RiskPage isDefaultLanguage={isDefaultLanguage} centerId={centerId} pageIndex={pageIndex} />;
    case 6:
      return <BehaviourPage isDefaultLanguage={isDefaultLanguage} centerId={centerId} pageIndex={pageIndex} />;
    case 7:
      return <SustainabilityPage isDefaultLanguage={isDefaultLanguage} centerId={centerId} pageIndex={pageIndex} />;
    case 8:
      return <LiquidityPage isDefaultLanguage={isDefaultLanguage} centerId={centerId} pageIndex={pageIndex} />;
    case 9:
      return <SavingsPage isDefaultLanguage={isDefaultLanguage} centerId={centerId} pageIndex={pageIndex} />;
    case 10:
      return <SharesPage isDefaultLanguage={isDefaultLanguage} centerId={centerId} pageIndex={pageIndex} />;
    case 11:
      return <DebtPage isDefaultLanguage={isDefaultLanguage} centerId={centerId} pageIndex={pageIndex} />;
    case 12:
      return <RiskLevelPage isDefaultLanguage={isDefaultLanguage} centerId={centerId} pageIndex={pageIndex} />;
    case 13:
      return <AgreementDetailsPage isDefaultLanguage={isDefaultLanguage} centerId={centerId} pageIndex={pageIndex} />;
    case 14:
      return <SavingSuggestionPage isDefaultLanguage={isDefaultLanguage} centerId={centerId} pageIndex={pageIndex} />;
    case 15:
      return <ExpectedValuePage isDefaultLanguage={isDefaultLanguage} centerId={centerId} pageIndex={pageIndex} />;
    case 16:
      return <CompleteSavingsPage isDefaultLanguage={isDefaultLanguage} centerId={centerId} pageIndex={pageIndex} />;
    case 17:
      return <SuitabilityReportPage isDefaultLanguage={isDefaultLanguage} centerId={centerId} pageIndex={pageIndex} />;
    case 18:
      return (
        <FinalPage isDefaultLanguage={isDefaultLanguage} centerId={centerId} pageIndex={pageIndex} avtaleGiroStatus={status} />
      );
    default:
      return <StartPage isDefaultLanguage={isDefaultLanguage} centerId={centerId} pageIndex={pageIndex} />;
  }
}

function IsValidId(id?: string) {
  return id !== undefined && Number(id) <= 18 && Number(id) > 0;
}

const inIframe = () => {
  try {
    return window.self !== window.top;
  } catch {
    return true;
  }
};

export default SavingsRobotPageClientComponent;
